import { queryNoTracking, update, updateAsFormData, downloadPost } from '../../services/DataService';
import { LEGACY_METADATA_VERSION, normalizeApiVersion } from '../../services/ApiVersionService';

// Data queries
const q_WorkflowDefinitions_Grid = queryNoTracking('WorkflowDefinitions_Grid', 0);
const q_WorkflowDefinitions_BoardVisible = queryNoTracking('WorkflowDefinitions_BoardVisible');
const q_WorkflowDefinition_GetDraft = queryNoTracking('WorkflowDefinition_GetDraft');
const q_WorkflowDefinition_GetVersion = queryNoTracking('WorkflowDefinition_GetVersion');
const q_WorkflowDefinition_VersionsGrid = queryNoTracking('WorkflowDefinition_VersionsGrid');
const q_WorkflowPolicyRulesWithRoles_ByVersion = queryNoTracking('WorkflowPolicyRulesWithRoles_ByVersion');
const q_WorkflowTriggers_ByVersion = queryNoTracking('WorkflowTriggers_ByVersion');
const q_WorkflowStages_ByVersion = queryNoTracking('WorkflowStages_ByVersion');
const q_WorkflowManualTransitions_ByVersion = queryNoTracking('WorkflowManualTransitions_ByVersion');
const q_WorkflowDefinition_WorkflowUserStatus = queryNoTracking('WorkflowDefinition_WorkflowUserStatus');
const q_WorkflowSecrets_ByDefinition = queryNoTracking('WorkflowSecrets_ByDefinition');
const q_WorkflowDefinition_Metadata = queryNoTracking('WorkflowDefinition_Metadata');
const q_WorkflowCardDefinitions_Grid = queryNoTracking('WorkflowCardDefinitions_Grid', 0);
const q_WorkflowCardDefinition = queryNoTracking('WorkflowCardDefinition');
const q_WorkflowCardDefinition_Workflows = queryNoTracking('WorkflowCardDefinition_Workflows');
const q_WorkflowTask_Board = queryNoTracking('WorkflowTask_Board');
const q_WorkflowTask_BoardStageCounts = queryNoTracking('WorkflowTask_BoardStageCounts');
const q_WorkflowTask_ReassignCandidates = queryNoTracking('WorkflowTask_ReassignCandidates');
const q_WorkflowRuns_Grid = queryNoTracking('WorkflowRuns_Grid', 0);
const q_WorkflowRuns_OperationsSummary = queryNoTracking('WorkflowRuns_OperationsSummary', 0);
const q_WorkflowRun_OperationsDetail = queryNoTracking('WorkflowRun_OperationsDetail', 0);
const q_WorkflowDefinitions_WithRuns = queryNoTracking('WorkflowDefinitions_WithRuns', 0);
const q_AuditEntries_PendingContentByEntityType = queryNoTracking('AuditEntries_PendingContentByEntityType', 0);
const q_WorkflowDataStore_Keys = queryNoTracking('WorkflowDataStore_Keys', 0);

export async function listManageWorkflowDefinitions() {
  return await q_WorkflowDefinitions_Grid.getAll();
}

export async function listWorkflowDefinitionsWithRuns() {
  return await q_WorkflowDefinitions_WithRuns.getAll();
}

export async function listPendingAuditContentByEntityType() {
  return await q_AuditEntries_PendingContentByEntityType.getAll();
}

export async function listBoardVisibleWorkflowDefinitions() {
  return await q_WorkflowDefinitions_BoardVisible.getAll();
}

export async function getWorkflowDraft(definitionId) {
  return await q_WorkflowDefinition_GetDraft.getFirstOrDefault({ id: definitionId });
}

export async function getWorkflowVersion(definitionId, versionID) {
  return await q_WorkflowDefinition_GetVersion.getFirstOrDefault({ id: definitionId, versionID });
}

export async function getWorkflowVersionsGrid(definitionId) {
  return await q_WorkflowDefinition_VersionsGrid.getAll({ id: definitionId });
}

export async function getWorkflowPolicyRulesWithRoles(versionIntId) {
  return await q_WorkflowPolicyRulesWithRoles_ByVersion.getAll({ workflowDefinitionVersionID: versionIntId });
}

export async function getWorkflowTriggers(versionIntId) {
  return await q_WorkflowTriggers_ByVersion.getAll({ workflowDefinitionVersionID: versionIntId });
}

export async function getWorkflowStages(versionIntId) {
  return await q_WorkflowStages_ByVersion.getAll({ workflowDefinitionVersionID: versionIntId });
}

export async function getWorkflowManualTransitions(versionIntId) {
  return await q_WorkflowManualTransitions_ByVersion.getAll({ workflowDefinitionVersionID: versionIntId });
}

export async function getWorkflowSecrets(definitionId) {
  return await q_WorkflowSecrets_ByDefinition.getAll({ workflowDefinitionID: definitionId });
}

export async function getWorkflowUserStatus() {
  return await q_WorkflowDefinition_WorkflowUserStatus.getFirstOrDefault({});
}

/**
 * Entity/field metadata for one API version. The workflow graph has no version, nodes
 * do: callers pass the metadataVersion of the node/setting/card they are editing
 * (getNodeMetadataVersion), defaulting to the legacy v2 set for anything authored before
 * versioning. The DataService cache keys on the request params, so each version is
 * fetched once per SPA session and never mixed with another.
 */
export async function getWorkflowMetadata({ apiVersion = LEGACY_METADATA_VERSION, forceRefresh = false } = {}) {
  const params = { apiVersion: normalizeApiVersion(apiVersion, LEGACY_METADATA_VERSION) };
  if (forceRefresh) {
    // Clear only this query's cache (all versions) so the refreshed value is reused by future callers.
    q_WorkflowDefinition_Metadata.clearCache?.();
  }
  return await q_WorkflowDefinition_Metadata.getFirstOrDefaultWithCache(params);
}

// Existing Data Store keys for the read node's path selector tree ({ defaultScope, items }).
// Always fetched fresh: the designer wants to see keys written moments ago by test runs.
export async function getWorkflowDataStoreKeys(workflowDefinitionID) {
  return await q_WorkflowDataStore_Keys.getFirstOrDefault({ workflowDefinitionID });
}

export async function listWorkflowCardDefinitions() {
  return await q_WorkflowCardDefinitions_Grid.getAll();
}

export async function getWorkflowCardDefinition(id) {
  return await q_WorkflowCardDefinition.getFirstOrDefault({ id });
}

// Workflows that use a card definition (explicit WorkflowCardDefinitionID, plus every
// same-entity-type workflow with no card when the card is the type's default), each with
// its live stages: [{ WorkflowDefinitionID, Name, EntityType, Explicit, Stages: [{ StageKey, StageName, SortOrder }] }].
// Pass entityType/isDefault for an unsaved card (id 0) so the designer can still preview.
export async function listWorkflowCardWorkflows({ id = 0, entityType = null, isDefault = false } = {}) {
  const params = { id: id ?? 0, isDefault: !!isDefault };
  // Query params serialize verbatim (null would arrive as the string "null"), so an
  // absent entity type is simply omitted.
  const safeEntityType = (entityType ?? '').toString().trim();
  if (safeEntityType) params.entityType = safeEntityType;
  return await q_WorkflowCardDefinition_Workflows.getAll(params);
}

export async function getWorkflowTaskBoard({
  scope = 'my',
  includeEmptyStages = false,
  workflowDefinitionID = null,
  workflowKey = null,
  includeClosedDays = 0
} = {}) {
  return await q_WorkflowTask_Board.getFirstOrDefault({
    scope,
    includeEmptyStages,
    workflowDefinitionID,
    workflowKey,
    includeClosedDays
  });
}

export async function getWorkflowBoardStageCounts({ workflowDefinitionID = null, includeClosedDays = 0, scope = 'my' } = {}) {
  return await q_WorkflowTask_BoardStageCounts.getFirstOrDefault({ workflowDefinitionID, includeClosedDays, scope });
}

export async function getWorkflowTaskReassignCandidates({ workflowRunID } = {}) {
  return await q_WorkflowTask_ReassignCandidates.getFirstOrDefault({ workflowRunID });
}

// Queryable data source for the Workflow Runs bound grid: the grid composes its column
// filters, sorting and infinite-scroll paging onto this query server-side. failureFilter
// ({ failedWithinHours, errorType, tookFailEdge }) narrows to runs with matching structured
// failure logs (ErrorCode/ErrorType) for outage forensics.
export function getWorkflowRunsGridQuery(searchText, failureFilter = null) {
  const params = {
    ...(searchText ? { searchText } : {}),
    ...(failureFilter?.failedWithinHours > 0 ? { failedWithinHours: failureFilter.failedWithinHours } : {}),
    ...(failureFilter?.errorType ? { errorType: failureFilter.errorType } : {}),
    ...(failureFilter?.tookFailEdge ? { tookFailEdge: true } : {}),
  };
  return q_WorkflowRuns_Grid.getAllAsQueryable(Object.keys(params).length ? params : null);
}

export async function getWorkflowRunsOperationsSummary() {
  return await q_WorkflowRuns_OperationsSummary.getFirstOrDefault();
}

export async function getWorkflowRunOperationsDetail(workflowRunID) {
  return await q_WorkflowRun_OperationsDetail.getFirstOrDefault({ workflowRunID });
}

// Updates
export async function createWorkflowDraft({ workflowKey, name, entityType, oneTimeOnly }) {
  return await update('WorkflowDefinition_CreateDraft', { workflowKey, name, entityType, oneTimeOnly }, true);
}

export async function saveWorkflowDraft(model) {
  return await update('WorkflowDefinition_SaveDraft', model, false);
}

export async function publishWorkflow(definitionId, { asTestMode = false } = {}) {
  return await update('WorkflowDefinition_Publish', { workflowDefinitionID: definitionId, asTestMode }, true);
}

export async function pauseWorkflow(definitionId) {
  return await update('WorkflowDefinition_Pause', { workflowDefinitionID: definitionId }, true);
}

export async function unpauseWorkflow(definitionId) {
  return await update('WorkflowDefinition_Unpause', { workflowDefinitionID: definitionId }, true);
}

export async function testModeWorkflow(definitionId) {
  return await update('WorkflowDefinition_TestMode', { workflowDefinitionID: definitionId }, true);
}

export async function disableWorkflow(definitionId) {
  return await update('WorkflowDefinition_Disable', { workflowDefinitionID: definitionId }, true);
}

export async function enableWorkflow(definitionId) {
  return await update('WorkflowDefinition_Enable', { workflowDefinitionID: definitionId }, true);
}

export async function prepareWorkflowPublish({ workflowDefinitionID, draftHash }) {
  return await update('WorkflowDefinition_PreparePublish', { workflowDefinitionID, draftHash }, true);
}

export async function getWorkflowMigrationStatus({ workflowDefinitionID, draftHash }) {
  return await update('WorkflowDefinition_GetMigrationStatus', { workflowDefinitionID, draftHash }, false);
}

export async function resolveWorkflowPublishMigration({ workflowDefinitionID, draftHash, resolutions }) {
  return await update('WorkflowDefinition_ResolvePublishMigration', { workflowDefinitionID, draftHash, resolutions }, true);
}

export async function completeWorkflowPublish({ workflowDefinitionID, draftHash, asTestMode = false }) {
  return await update('WorkflowDefinition_CompletePublish', { workflowDefinitionID, draftHash, asTestMode }, true);
}

export async function validateWorkflowDraft(definitionId) {
  return await update('WorkflowDefinition_ValidateDraft', { workflowDefinitionID: definitionId }, false);
}

/**
 * Rewrites node / custom setting / card configs to the latest API metadata version
 * (Api_V2_ -> Api_V3_ views, renamed fields, metadataVersion). Pure: nothing is stored —
 * the caller applies the returned configs and saves through its normal path.
 * `items`: [{ id, config }] (config = plain object). Returns the server model with
 * `items[].config` parsed back to an object.
 */
export async function upgradeWorkflowMetadataVersion({ workflowEntityType, triggerEntityType, items } = {}) {
  const result = await update('WorkflowDefinition_UpgradeMetadataVersion', {
    workflowEntityType: workflowEntityType ?? '',
    triggerEntityType: triggerEntityType ?? '',
    items: (items ?? []).map(item => ({
      id: `${item?.id ?? ''}`,
      configJson: JSON.stringify(item?.config ?? {}),
    })),
  }, false);
  if (!result || result.IsFrameworkException) {
    return { ok: false, error: result?.message ?? result?.Message ?? '', items: [] };
  }
  const ok = result.ok ?? result.Ok ?? false;
  const error = result.error ?? result.Error ?? '';
  const rows = result.items ?? result.Items ?? [];
  return {
    ok: !!ok,
    error,
    items: rows.map(row => {
      let config = {};
      try {
        config = JSON.parse(row.configJson ?? row.ConfigJson ?? '{}') ?? {};
      } catch {
        config = {};
      }
      return {
        id: row.id ?? row.Id ?? '',
        fromVersion: row.fromVersion ?? row.FromVersion ?? '',
        toVersion: row.toVersion ?? row.ToVersion ?? '',
        upgraded: !!(row.upgraded ?? row.Upgraded),
        config,
        changes: (row.changes ?? row.Changes ?? []).map(c => ({
          path: c.path ?? c.Path ?? '',
          from: c.from ?? c.From ?? '',
          to: c.to ?? c.To ?? '',
          reason: c.reason ?? c.Reason ?? '',
        })),
        notes: row.notes ?? row.Notes ?? [],
      };
    }),
  };
}

export async function restoreWorkflowToVersion(definitionId, versionGuid) {
  return await update(
    'WorkflowDefinition_RestoreToVersion',
    { workflowDefinitionID: definitionId, workflowDefinitionVersionID: versionGuid },
    true
  );
}

export async function createWorkflowUser() {
  return await update('WorkflowDefinition_CreateWorkflowUser', {}, true);
}

/**
 * Folds runtime property-casing misses into the test outcome. A wrong-case property read
 * (row.invoiceLines vs InvoiceLines) silently evaluates to undefined in JavaScript — the
 * script "passes" with wrong data — so the harness probes casing during tests and any miss
 * must surface as a plain test failure to every caller.
 */
export const foldCasingMissesIntoResult = (res) => {
  const misses = res?.casingMisses ?? res?.CasingMisses ?? [];
  if (!Array.isArray(misses) || misses.length === 0) return res;
  const casingError = `Property casing check failed. ${misses.join(' ')}`;
  const existing = (res?.error ?? res?.Error ?? '').toString();
  const error = existing ? `${existing}\n${casingError}` : casingError;
  return { ...res, success: false, Success: false, error, Error: error };
};

export async function testWorkflowExpression({ expression, templateValues, variablesJson, localsJson } = {}) {
  const res = await update(
    'WorkflowNodeTest_Expression',
    {
      expression: expression ?? '',
      templateValues: templateValues ?? {},
      variablesJson: variablesJson ?? '{}',
      // Flat JSON object of dotted ctx paths to values bound as first-class locals —
      // the highest-precedence context layer in the JS harness.
      localsJson: localsJson ?? '{}',
      // This endpoint only serves tests, so casing detection is always on.
      detectCasingMisses: true,
    },
    false
  );
  return foldCasingMissesIntoResult(res);
}

export async function testWorkflowSetVariable({
  name,
  valueMode,
  valueTemplate,
  script,
  variablesJson,
  templateValues,
  // Flat JSON object of dotted ctx paths to values (e.g. {"trigger.rows": [...]}) bound
  // as first-class locals — the highest-precedence context layer in the JS harness.
  localsJson,
} = {}) {
  const res = await update(
    'WorkflowNodeTest_SetVariable',
    {
      name: name ?? '',
      valueMode: valueMode ?? 'template',
      valueTemplate: valueTemplate ?? '',
      script: script ?? '',
      variablesJson: variablesJson ?? '{}',
      templateValues: templateValues ?? {},
      localsJson: localsJson ?? '{}',
      // This endpoint only serves tests, so casing detection is always on.
      detectCasingMisses: true,
    },
    false
  );
  return foldCasingMissesIntoResult(res);
}

const normalizeWorkflowCustomSettingsEvaluateResult = (result) => {
  const rows = result?.rows ?? result?.Rows ?? [];
  return {
    ok: result?.ok ?? result?.Ok ?? false,
    error: result?.error ?? result?.Error ?? '',
    rows: (Array.isArray(rows) ? rows : []).map(row => ({
      index: row?.index ?? row?.Index ?? 0,
      key: row?.key ?? row?.Key ?? '',
      ok: row?.ok ?? row?.Ok ?? false,
      valueJson: row?.valueJson ?? row?.ValueJson ?? '',
      error: row?.error ?? row?.Error ?? '',
      inputVariablesJson: row?.inputVariablesJson ?? row?.InputVariablesJson ?? '{}',
      inputTemplateValues: row?.inputTemplateValues ?? row?.InputTemplateValues ?? {},
    })),
    variablesJson: result?.variablesJson ?? result?.VariablesJson ?? '{}',
    variables: result?.variables ?? result?.Variables ?? {},
    templateValues: result?.templateValues ?? result?.TemplateValues ?? {},
  };
};

export async function testWorkflowCustomSettings(model = {}) {
  const result = await update('WorkflowNodeTest_CustomSettingsEvaluate', {
    workflowEntityType: model.workflowEntityType ?? '',
    workflowEntityId: model.workflowEntityId ?? '',
    triggerEntityType: model.triggerEntityType ?? '',
    triggerEntityId: model.triggerEntityId ?? '',
    triggerType: model.triggerType ?? '',
    initialVariablesJson: model.initialVariablesJson ?? '{}',
    templateValues: model.templateValues ?? {},
    customSettings: model.customSettings ?? [],
  }, false);
  return normalizeWorkflowCustomSettingsEvaluateResult(result);
}

export async function previewWorkflowHttpEndpoint(model = {}) {
  return await update('WorkflowNodeTest_HttpEndpointPreview', model, false);
}

export async function executeWorkflowHttpEndpoint(model = {}) {
  return await update('WorkflowNodeTest_HttpEndpointExecute', model, false);
}

export async function startWorkflowDraftSimulation(model = {}) {
  return await update('WorkflowNodeTest_DraftSimulationStart', model, false);
}

export async function buildWorkflowSimulatedAuditEvent(model = {}) {
  return await update('WorkflowNodeTest_BuildSimulatedAuditEvent', model, false);
}

export async function startWorkflowTestRun(model = {}) {
  return await update('WorkflowTestRun_Start', model, false);
}

export async function previewWorkflowTestRunTimer(model = {}) {
  return await update('WorkflowTestRun_TimerPreview', model, false);
}

export async function stepWorkflowDraftSimulation(model = {}) {
  return await update('WorkflowNodeTest_DraftSimulationStep', model, false);
}

export async function saveWorkflowSecret(definitionId, { secretKey, name, value }) {
  return await update(
    'WorkflowSecret_Save',
    { workflowDefinitionID: definitionId, secretKey, name, value },
    true
  );
}

export async function saveWorkflowCardDefinition(model) {
  const result = await update('WorkflowCardDefinition_Save', model ?? {}, true);
  q_WorkflowCardDefinitions_Grid.clearCache?.();
  q_WorkflowCardDefinition.clearCache?.();
  return result;
}

export async function deactivateWorkflowCardDefinition(id) {
  const result = await update('WorkflowCardDefinition_Deactivate', { id }, true);
  q_WorkflowCardDefinitions_Grid.clearCache?.();
  q_WorkflowCardDefinition.clearCache?.();
  return result;
}

export async function setDefaultWorkflowCardDefinition(id) {
  const result = await update('WorkflowCardDefinition_SetDefault', { id }, true);
  q_WorkflowCardDefinitions_Grid.clearCache?.();
  q_WorkflowCardDefinition.clearCache?.();
  return result;
}

export async function previewWorkflowCard(model) {
  return await update('WorkflowCard_Preview', model ?? {}, false);
}

export async function exportWorkflowPackage(workflowDefinitionIDs) {
  return await downloadPost(
    'api/Update/WorkflowDefinition_ExportPackage',
    { workflowDefinitionIDs: workflowDefinitionIDs ?? [] },
    'Export Workflows'
  );
}

export async function analyzeWorkflowImportPackage(file) {
  return await updateAsFormData('WorkflowDefinition_AnalyzeImportPackage', { file });
}

export async function importWorkflowPackage(file, model) {
  return await updateAsFormData('WorkflowDefinition_ImportPackage', {
    file,
    modelJson: JSON.stringify(model ?? {})
  });
}

export async function getWorkflowReferenceSelectionList(model) {
  return await update('WorkflowDefinition_ReferenceSelectionList', model ?? {}, false);
}

export async function deleteWorkflowDefinition(definitionId) {
  // Not the generic DeleteEntity: the workflow host holds engine-side state for the definition
  // that has to be cancelled too, and the tenant may need releasing from engine polling.
  return await update('WorkflowDefinition_Delete', { workflowDefinitionID: definitionId }, true);
}

export async function workflowTaskManualTransition({
  workflowRunID,
  toStageKey,
  expectedFromStageKey,
  expectedFromNodeId,
  targetCluster,
}) {
  return await update(
    'WorkflowTask_ManualTransition',
    { workflowRunID, toStageKey, expectedFromStageKey, expectedFromNodeId, targetCluster },
    false
  );
}

export async function workflowTaskReassign({
  workflowRunID,
  assignedRepID,
}) {
  return await update(
    'WorkflowTask_Reassign',
    { workflowRunID, assignedRepID },
    false
  );
}

export async function workflowRunRepairCommand(model = {}) {
  return await update('WorkflowRun_RepairCommand', model, true);
}

// Bulk outage recovery: applies one repair command to a selection of runs and returns
// per-run results (one failing run never aborts the batch).
export async function workflowRunBulkRepairCommand({ workflowRunIDs = [], command, reason } = {}) {
  return await update('WorkflowRun_BulkRepairCommand', { workflowRunIDs, command, reason }, true);
}

// Read-only diagnostic snapshot of engine queue/lease/dead-letter state for one run.
// Fetched on demand from Repair Mode (never as part of the general run detail load) because it
// calls into the workflow runtime service.
export async function workflowRunRuntimeStatus({ workflowRunID, auditEntryId = null } = {}) {
  return await update('WorkflowRun_RuntimeStatus', { workflowRunID, auditEntryId }, false);
}

